<template>
  <v-container
    fill-height
    fluid
  >
    <v-row class="align-center justify-center">
      <v-col
        sm="8"
        md="6"
        lg="4"
      >
        <v-card
          v-show="!showDialog"
          class="elevation-12 pa-4 text-center"
        >
          <v-card-text>
            <form-wrapper :validator="$v.cashregister">
              <v-form
                ref="form"
                @submit.prevent="checkCode"
              >
                <template v-if="_.get(cashRegister, 'store')">
                  <img
                    v-if="_.get(cashRegister, 'store.storeLogoUrl')"
                    :src="cashRegister.store.storeLogoUrl"
                    class="mb-3"
                    width="200"
                  >
                  <img
                    v-else
                    class="mb-3"
                    src="/static/images/vaporshop-logo.jpg"
                    width="200"
                  >
                </template>

                <h1 class="font-weight-light mb-6">{{ $t('Installation') }}</h1>
                <p class="mb-6">{{ $t('This cash register needs to be initialized first. Please fill in the unique code in the form below and follow further instructions.') }}</p>

                <form-group name="identifier">
                  <template slot-scope="{ attrs }">
                    <v-text-field
                      id="identifier"
                      v-bind="attrs"
                      v-model="cashregister.identifier"
                      :label="$t('Cash Register Code')"
                      autocomplete="off"
                      class="input-identifier"
                      filled
                      single-line
                      @blur="$v.cashregister.identifier.$touch()"
                      @input="$v.cashregister.identifier.$touch()"
                    />
                  </template>
                </form-group>

                <v-btn
                  :loading="isChecking"
                  block
                  color="primary"
                  dark
                  large
                  @click="checkCode"
                >{{ $t('Check Code') }}</v-btn>
              </v-form>
            </form-wrapper>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog
      v-if="showDialog"
      v-model="showDialog"
      eager
      width="600"
    >
      <v-card>
        <v-card-text>
          <template v-if="previewData && (!previewData.installationDate || devMode)">
            <div class="text-center pt-4 pb-4 mb-4">
              <img
                v-if="_.get(previewData, 'store.storeLogoUrl')"
                :src="previewData.store.storeLogoUrl"
                class="mb-3"
                width="200"
              >
              <img
                v-else
                class="mb-3"
                src="/static/images/vaporshop-logo.jpg"
                width="200"
              >
              <h2 class="text-h6">{{ $t('Verify if the information below is correct.') }}</h2>
            </div>
          </template>

          <template v-else>
            <v-container
              class="pa-1"
              fluid
            >
              <v-alert
                class="text-center mt-4 mb-4"
                color="primary"
                outlined
                text
              >
                {{ `${$t('This cash register has already been installed.')} ${$t('Contact an administrator.')}` }}
              </v-alert>
            </v-container>
          </template>

          <v-divider />
          <v-list dense>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">{{ $t('Cash Register') }}</v-list-item-content>
              <v-list-item-content class="justify-end">{{ previewData.description }}</v-list-item-content>
            </v-list-item>

            <v-divider class="mt-2" />

            <v-list-item class="mt-2">
              <v-list-item-content class="font-weight-bold">{{ $t('Store') }}</v-list-item-content>
              <v-list-item-content class="justify-end text-right">
                {{ previewData.store.name }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content class="font-weight-bold">{{ $t('Address') }}</v-list-item-content>
              <v-list-item-content class="justify-end text-right">
                {{ previewData.store.address }}<br>{{ previewData.store.postalCode }} {{ previewData.store.city }}
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-content class="font-weight-bold">{{ $t('E-mail') }}</v-list-item-content>
              <v-list-item-content class="justify-end text-right">
                {{ previewData.store.email }}
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-content class="font-weight-bold">{{ $t('Phone') }}</v-list-item-content>
              <v-list-item-content class="justify-end text-right">
                {{ previewData.store.phone }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider v-if="previewData && (!previewData.installationDate || devMode)" />
        <v-card-actions v-if="previewData && (!previewData.installationDate || devMode)">
          <v-btn
            class="white--text"
            color="error"
            @click="showDialog = false"
          >
            <v-icon
              class="ml-1 mr-2"
              dark
              left
              small
            >fal fa-times</v-icon>
            {{ $t('Cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            :loading="isSaving"
            class="white--text"
            color="success"
            @click="save"
          >
            <v-icon
              class="ml-1 mr-2"
              dark
              left
              small
            >fal fa-thumbs-up</v-icon>
            {{ $t('This is correct') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import validationMixin from '@/utils/mixins/validationMixin'
import cashRegisterService from '@/services/cash-registers'
import camelcaseKeys from 'camelcase-keys'
import store from '@/store'
import variables from '@/utils/variables'

export default {
  mixins: [
    validationMixin
  ],

  metaInfo () {
    return { title: this.pageTitle }
  },

  data () {
    return {
      cashregister: {
        identifier: ''
      },
      isChecking: false,
      isSaving: false,
      pageTitle: Vue.i18n.translate('Installation'),
      previewData: null,
      showDialog: false
    }
  },

  validations: {
    cashregister: {
      identifier: {
        required
      }
    }
  },

  computed: {
    ...mapState(['cashRegister']),
    devMode () {
      return variables.devMode
    }
  },

  methods: {
    save () {
      this.isSaving = true
      // dispatch save action
      this.$store.dispatch('SAVE_CASH_REGISTER', this.cashregister.identifier)
        .then((data) => {
          this.helpers.toastSuccess(data.message)
          this.$router.push('/')
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally((data) => {
          this.isSaving = false
        })
    },
    checkCode () {
      if (!this.validate()) return
      this.isChecking = true

      cashRegisterService.get(this.cashregister.identifier)
        .then((data) => {
          // show confirm dialog with cashregister data
          this.previewData = camelcaseKeys(data.data.cash_register, { deep: true })
          this.showDialog = true
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => {
          this.isChecking = false
        })
    }
  },

  beforeRouteEnter (to, from, next) {
    // redirect to homepage if the cashregister has already been installed
    const isCashRegisterConfigured = store.getters.isCashRegisterConfigured
    if (isCashRegisterConfigured) {
      next('/')
    } else {
      next()
    }
  }
}
</script>

<style lang="scss" scoped>
.input-identifier {
  font-size: 2em;
}

.v-card__actions {
  padding: 16px;
}
</style>



// WEBPACK FOOTER //
// src/views/Installation.vue